<script setup lang="ts">
  import { onMounted, onUnmounted } from 'vue';
  // Components
  import ColoredButton from '@/shared/ui/buttons/ColoredButton.vue';
  import IconImage from '@/shared/ui/common/IconImage.vue';
  // Icons
  import filterIcon from '@/shared/assets/icons/Filter.svg';
  import filterWhite from '@/shared/assets/icons/FilterWhite.svg';
  import orderIcon from '@/shared/assets/icons/Order.svg';
  import orderWhite from '@/shared/assets/icons/OrderWhite.svg';
  import trashIcon from '@/shared/assets/icons/TrashBlackIcon.svg';
  import pencilIcon from '@/shared/assets/icons/PencilBlack.svg';
  // Composables
  import { useFilterAndSort } from '@/shared/lib';
  // interfaces
  import type { OrderOptions } from '@/shared/lib';

  const props = withDefaults(
    defineProps<{
      hasSelected?: boolean;
      selectedCount?: number;
      selectedDocuments?: number[];
      orderOptions: OrderOptions[];
      isFilterActive?: boolean;
      currentSort?: string;
      currentOrder?: string;
      hasSort?: boolean;
      hasFilter?: boolean;
      hasDelete?: boolean;
      deleteDisabled?: boolean;
      hasEdit?: boolean;
      editDisabled?: boolean;
    }>(),
    {
      hasSort: true,
      hasFilter: true,
      hasDelete: true,
      deleteDisabled: false,
      hasEdit: false,
      editDisabled: false,
    }
  );

  const emit = defineEmits(['update:sort', 'update:filter', 'filter-clicked', 'delete', 'edit']);

  const {
    isOrderSelected,
    isOrderDropdownOpen,
    selectedOption,
    dropdownPosition,
    orderButtonRef,
    handleOrder,
    handleFilter,
    sortOptions,
    handleClickOutside,
    hideModalOverlay,
  } = useFilterAndSort(emit, {
    sort: () => props.currentSort,
    order: () => props.currentOrder,
    isActive: () => props.currentSort !== undefined && props.currentOrder !== undefined,
  });

  const handleSortOptions = (sortCamp: string, orderFlow: string) => {
    sortOptions(sortCamp, orderFlow);
  };

  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    // Without this, unmounting with the dropdown open leaves the full screen
    // overlay in the DOM and the scroll locked, and the screen stops responding.
    hideModalOverlay();
  });
</script>

<template>
  <div class="flex shrink-0 items-end gap-4 relative">
    <div
      v-if="hasSort"
      class="relative"
    >
      <div ref="orderButtonRef">
        <ColoredButton
          v-if="isOrderSelected"
          text="Ordenar por"
          :icon="orderWhite"
          icon-alt="Ícone de ordenação"
          :outlined="false"
          @click="handleOrder"
        />
        <ColoredButton
          v-else
          text="Ordenar por"
          :icon="orderIcon"
          icon-alt="Ícone de ordenação"
          :outlined="true"
          @click="handleOrder"
        />
      </div>

      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="transform opacity-0"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="transform opacity-0"
      >
        <div
          v-if="isOrderDropdownOpen"
          id="order-dropdown"
          class="fixed max-w-80 p-4 bg-white-01 rounded-2xl shadow-2xl z-50"
          :style="{ top: dropdownPosition.top + 'px', right: dropdownPosition.right + 'px' }"
        >
          <div class="py-1">
            <button
              v-for="option in props.orderOptions"
              :key="option.label"
              class="block w-full text-left px-4 py-2 text-md text-gray-07 font-medium"
              :class="{
                'bg-gray-02 rounded-lg':
                  selectedOption?.sort === option.sort && selectedOption?.order === option.order,
              }"
              @click="handleSortOptions(option.sort, option.order)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </transition>
    </div>
    <template v-if="hasFilter">
      <ColoredButton
        v-if="isFilterActive"
        text="Filtrar"
        :icon="filterWhite"
        icon-alt="Ícone de filtro"
        :outlined="false"
        @click="handleFilter"
      />
      <ColoredButton
        v-else
        text="Filtrar"
        :icon="filterIcon"
        icon-alt="Ícone de filtro"
        :outlined="true"
        @click="handleFilter"
      />
    </template>

    <button
      v-if="hasEdit"
      class="px-4 py-3 rounded-lg font-medium flex items-center md:text-base text-xs md:gap-3 gap-1 h-10.5 bg-[var(--theme-secondary)] text-white disabled:opacity-50"
      :disabled="editDisabled"
      :style="editDisabled ? 'cursor: not-allowed !important;' : ''"
      @click="emit('edit')"
    >
      <IconImage
        :src="pencilIcon"
        alt="Ícone de edição"
      />
      <span>Editar</span>
    </button>

    <button
      v-if="hasDelete"
      class="px-4 py-3 rounded-lg font-medium flex items-center md:text-base text-xs md:gap-3 gap-1 h-10.5 border-1 border-gray-02 text-gray-07"
      :disabled="deleteDisabled"
      :style="deleteDisabled ? 'cursor: not-allowed !important;' : ''"
      @click="emit('delete')"
    >
      <IconImage
        :src="trashIcon"
        alt="Ícone de exclusão"
        :class="{ 'opacity-50': deleteDisabled }"
      />
      <span :class="{ 'opacity-50': deleteDisabled }">Deletar</span>
    </button>
  </div>
</template>
